import { IconButton } from "@mui/material";
import { AlertTriangle, Trash2 } from "lucide-react";
import { ReactNode, useEffect, useRef } from "react";
import { X } from "react-feather";

type Props = {
  title: string | any;
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
  cancelText?: string;
  confirmText?: string;
  fixedWidth?: string;
  /** Use "danger" for delete / destructive actions (red button + trash icon) */
  variant?: "default" | "danger";
};

export default function NewConfirmationModal({
  isOpen,
  title,
  children,
  cancelText,
  confirmText,
  fixedWidth,
  variant = "default",
  onClose,
  onConfirm,
}: Props) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const isDanger = variant === "danger";

  useEffect(() => {
    if (isOpen) {
      const modalElement = modalRef.current;

      if (!modalElement) {
        throw new Error("Modal ref returned null");
      }

      const focusableElements = modalElement.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLDivElement;
      const lastElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLDivElement;

      const handleTabKeyPress = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (event.key === "Tab") {
          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          } else if (
            !event.shiftKey &&
            document.activeElement === lastElement
          ) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      };

      const handleEscapeKeyPress = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          onClose();
        }
      };

      modalElement.addEventListener("keydown", handleTabKeyPress);
      modalElement.addEventListener("keydown", handleEscapeKeyPress);

      return () => {
        modalElement.removeEventListener("keydown", handleTabKeyPress);
        modalElement.removeEventListener("keydown", handleEscapeKeyPress);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="modal-bg h-screen w-screen"
      role="dialog"
      id="new-confirmation-modal"
      ref={modalRef}
    >
      <div
        className={`modal-dialog modal-dialog-centered pointer-events-none relative ${
          fixedWidth?.length
            ? fixedWidth
            : "w-[92%] max-w-md sm:w-8/12 md:w-6/12 lg:w-4/12"
        }`}
      >
        <div className="modal-content pointer-events-auto relative flex w-full animate-fade-in flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-current shadow-xl shadow-slate-200/50 outline-none">
          <div className="modal-header flex flex-shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div className="flex min-w-0 flex-1 items-start gap-2.5">
              <span
                className={`mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                  isDanger
                    ? "bg-red-50 text-red-600"
                    : "bg-amber-50 text-amber-600"
                }`}
              >
                {isDanger ? <Trash2 size={16} /> : <AlertTriangle size={16} />}
              </span>
              <h1 className="pt-1 text-sm font-medium leading-snug text-slate-900">
                {title}
              </h1>
            </div>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: "#64748b" }}
            >
              <X size={18} />
            </IconButton>
          </div>

          <div className="modal-body relative px-4 pb-4 pt-3">
            {children ? (
              <div className="max-h-[70vh] overflow-y-auto text-sm leading-relaxed text-slate-600">
                {children}
              </div>
            ) : null}

            <div
              className={`flex justify-end gap-2 ${children ? "mt-5" : "mt-1"}`}
            >
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-3.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                autoFocus
              >
                {cancelText ? cancelText : "Cancel"}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex h-8 items-center justify-center gap-1.5 rounded-lg px-3.5 text-xs font-semibold text-white transition ${
                  isDanger
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-[#0159DC] hover:bg-blue-700"
                }`}
              >
                {isDanger ? <Trash2 size={13} /> : null}
                {confirmText ? confirmText : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
